import { microwave } from '@round/api';
import { useAbortableEffect, useDataState } from '@round/utils';
import { useCallback } from 'react';

type Options = {
    enabled?: boolean;
};

export function useMicrowaveInfluencerAccounts(
    { page, page_size, id }: microwave.GetMicrowaveInfluencerAccountsParams,
    { enabled = true }: Options = {}
) {
    const [state, setState] = useDataState<microwave.MicrowaveInfluencerAccount[]>();

    const fetchInfluencerAccounts = useCallback(
        async (signal: AbortSignal) => {
            setState('loading');
            try {
                const response = await microwave.getMicrowaveInfluencerAccounts({ page, page_size, id }, { signal });

                if (response.status !== 200) {
                    setState('error', { error: 'Could not fetch microwave influencer accounts' });
                    return;
                }

                setState('success', { data: response.data.results });
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                const errorMessage = e instanceof Error ? e.message : 'Could not fetch microwave influencer accounts';
                setState('error', { error: errorMessage });
            }
        },
        [setState, page, page_size, id]
    );

    useAbortableEffect(
        (signal) => {
            if (!enabled) {
                return;
            }

            void fetchInfluencerAccounts(signal);
        },
        [enabled, fetchInfluencerAccounts]
    );

    const reset = () => setState('idle', { data: null });

    return {
        state,
        reset,
    };
}
